
import { useParams } from 'react-router-dom';
import { useEmail } from '@/context/EmailContext';
import PageTransition from '@/components/layout/PageTransition';
import EmailFolderHeader from '@/components/email/folder/EmailFolderHeader';
import EmailFolderContent from '@/components/email/folder/EmailFolderContent';
import EmailFolderError from '@/components/email/folder/EmailFolderError';
import EmailFolderLoading from '@/components/email/folder/EmailFolderLoading';
import { useEmailFolderActions } from '@/hooks/email/useEmailFolderActions';
import { useEffect, useRef, useCallback } from 'react';

interface EmailFolderViewProps {
  folder?: string;
}

const EmailFolderView = ({ folder: folderProp }: EmailFolderViewProps) => {
  const { folder: folderParam = 'inbox' } = useParams<{ folder: string }>();
  const currentFolder = folderProp || folderParam;
  const lastFolderRef = useRef<string>('');
  
  const { 
    emails,
    statistics,
    loading, 
    error,
    isDbReady,
    setCurrentFolder,
    loadFolderEmails,
    refreshEmails
  } = useEmail();

  // Set the current folder and trigger email loading only when folder changes
  useEffect(() => {
    if (lastFolderRef.current === currentFolder) {
      return; // Skip if folder hasn't changed
    }

    lastFolderRef.current = currentFolder;
    setCurrentFolder(currentFolder);

    // EmailFolderContent will handle loading based on its pagination state
  }, [currentFolder, setCurrentFolder]);

  const {
    selectedEmails,
    setSelectedEmails,
    handleMarkAsRead,
    handleMarkAsUnread,
    handleDelete,
    handleRefresh
  } = useEmailFolderActions();

  const handleSelectAll = (selected: boolean) => {
    setSelectedEmails(selected ? emails.map((email) => email.id) : []);
  };

  // Create a refresh handler that uses the context's refreshEmails
  const handleRefreshWithFolder = useCallback(() => {
    refreshEmails(); // Call without arguments as per context signature
  }, [refreshEmails]);

  // Show error state if database failed to load
  if (error) {
    return <EmailFolderError folderName={currentFolder} onRefresh={handleRefreshWithFolder} />;
  }

  // Reserve the full-page loader for initial mailbox bootstrap. Folder changes
  // keep the shell mounted while EmailFolderContent loads its own rows.
  if (!isDbReady) {
    return <EmailFolderLoading folderName={currentFolder} isDbReady={isDbReady} />;
  }

  return (
    <PageTransition>
      <div className="flex flex-col h-full">
        <EmailFolderHeader
          folderName={currentFolder}
          emailCount={statistics.total[currentFolder] || 0}
          selectedEmails={selectedEmails}
          loading={loading}
          currentFolder={currentFolder}
          onMarkAsRead={handleMarkAsRead}
          onMarkAsUnread={handleMarkAsUnread}
          onDelete={() => handleDelete(currentFolder)}
          onRefresh={handleRefreshWithFolder}
          onSelectAll={handleSelectAll}
        />

        <EmailFolderContent 
          currentFolder={currentFolder}
          isDbReady={isDbReady}
          selectedEmails={selectedEmails}
          setSelectedEmails={setSelectedEmails}
        />
      </div>
    </PageTransition>
  );
};

export default EmailFolderView;
